import * as React from 'react';

import { translate } from '~l10n/client';

import { IconButton } from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/standard';

import { useResultQuestionState } from '../ActivityResult/ActivityResultQuestionProvider';

interface ResultsQuestionNavigationProps {
  onBackToResultsClick?: () => void;
  onPrevClick?: (order: number) => void;
  onNextClick?: (order: number) => void;
  isNavigationDisabled: boolean;
  isNavigationVisible?: boolean;
  isBackToResultsVisible: boolean;
}

export function ResultsQuestionNavigation({
  onBackToResultsClick,
  onPrevClick,
  onNextClick,
  isNavigationDisabled,
  isNavigationVisible = true,
  isBackToResultsVisible,
}: ResultsQuestionNavigationProps): React.ReactElement {
  const questionContext = useResultQuestionState();
  const { question = { order: undefined }, totalNumQuestions } =
    questionContext;
  const currentOrder = question?.order ?? 0;

  const getPreviousQuestionOrder = (): number => {
    return Math.max(1, currentOrder - 1);
  };
  const getNextQuestionOrder = (): number => {
    return Math.min(currentOrder + 1, totalNumQuestions);
  };
  const hasPreviousQuestion = (): boolean => {
    return getPreviousQuestionOrder() < currentOrder;
  };
  const hasNextQuestion = (): boolean => {
    return getNextQuestionOrder() > currentOrder;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between py-6 md:py-4">
      <div className="flex flex-1">
        {isBackToResultsVisible && (
          <button
            className="px-0 py-2 font-sans text-sm font-600 leading-8 text-cyan-700 bg-transparent border-0 cursor-pointer"
            onClick={onBackToResultsClick as () => void}
            data-testid="back-to-results"
          >
            <div className="flex items-center">
              <div className="hidden sm:flex">
                <IconChevron direction="left" />
              </div>
              {translate('Back to Results Table')}
            </div>
          </button>
        )}
      </div>
      {isNavigationVisible ? (
        <div className="flex items-center pt-4 sm:pt-0">
          <div>
            <IconButton
              appearance="secondary"
              onClick={() => onPrevClick(getPreviousQuestionOrder())}
              disabled={!hasPreviousQuestion() || isNavigationDisabled}
              aria-label={translate('Previous Question')}
            >
              <IconChevron direction="left" />
            </IconButton>
          </div>
          <div className="px-10">
            <span className="text-sm font-600 leading-8 text-grey-800">
              {translate('{0} of {1}').format(currentOrder, totalNumQuestions)}
            </span>
          </div>
          <div>
            <IconButton
              appearance="secondary"
              onClick={() => onNextClick(getNextQuestionOrder())}
              disabled={!hasNextQuestion() || isNavigationDisabled}
              aria-label={translate('Next Question')}
            >
              <IconChevron direction="right" />
            </IconButton>
          </div>
        </div>
      ) : null}
      <div className="hidden md:flex flex-1" />
    </div>
  );
}
